import { createBaseApi } from '../baseApi';

// Create a base cart API
const cartApi = createBaseApi('cartApi');

// Create the enhanced cart API with endpoints
export const enhancedCartApi = cartApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get the current user's cart
    getCart: builder.query({
      query: () => '/carts/me',
      providesTags: ['Cart'],
    }),
    
    // Create a new cart (usually not needed as getCart creates one if needed)
    createCart: builder.mutation({
      query: () => ({
        url: '/carts',
        method: 'POST',
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Add an item to the cart
    addCartItem: builder.mutation({
      query: (cartItem) => ({
        url: '/carts/items',
        method: 'POST',
        body: cartItem,
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Replace a rental period for an existing item
    replaceRentalPeriod: builder.mutation({
      query: ({ itemIndex, rentalPeriod }) => ({
        url: '/carts/rental-period',
        method: 'PUT',
        body: {
          itemIndex,
          rentalPeriod,
        },
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Update a cart item
    updateCartItem: builder.mutation({
      query: ({ itemIndex, cartItem }) => ({
        url: '/carts/items',
        method: 'PUT',
        body: {
          itemIndex,
          cartItem,
        },
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Update delivery method for a cart item
    updateDeliveryMethod: builder.mutation({
      query: ({ itemIndex, deliveryMethod }) => ({
        url: '/carts/delivery-method',
        method: 'PUT',
        body: {
          itemIndex,
          deliveryMethod,
        },
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Remove an item from the cart
    removeCartItem: builder.mutation({
      query: (itemId) => ({
        url: `/carts/item/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Clear all items from the cart
    clearCart: builder.mutation({
      query: () => ({
        url: '/carts/clear',
        method: 'PUT',
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Process cart checkout
    checkout: builder.mutation({
      query: (checkoutData) => ({
        url: '/carts/checkout',
        method: 'POST',
        body: checkoutData,
      }),
      invalidatesTags: ['Cart'],
    }),
    
    // Process currency-specific checkout
    checkoutByCurrency: builder.mutation({
      query: (checkoutData) => ({
        url: '/carts/checkout-by-currency',
        method: 'POST',
        body: checkoutData,
      }),
      invalidatesTags: ['Cart'],
    }),
  }),
});

// Export hooks for using the API
export const {
  useGetCartQuery,
  useCreateCartMutation,
  useAddCartItemMutation,
  useUpdateCartItemMutation,
  useUpdateDeliveryMethodMutation,
  useRemoveCartItemMutation,
  useClearCartMutation,
  useCheckoutMutation,
  useCheckoutByCurrencyMutation,
  useReplaceRentalPeriodMutation,
} = enhancedCartApi;

// Helper function to map cart items to the format expected by the frontend
export const mapCartItems = (cart) => {
  if (!cart || !cart.cartItems) return []; // Return empty array if no cart or items
  
  return cart.cartItems.map(item => {
    const product = item.product;
    if (!product) return null; // Should ideally not happen if data is clean
    
    const mappedItem = {
      ...product,
      id: product.id, // Product ID
      cartItemId: item.id, // This is the unique ID of the cartItem component itself
      type: item.type, 
      quantity: item.quantity,
      cartItemIndex: cart.cartItems.indexOf(item), // Index within the original backend array
      productOptions: item.productOptions || [],
      customNote: item.customNote,
      merchant: item.merchant,
      deliveryMethod: item.deliveryMethod || 'shipping'
    };
    
    if (item.type === 'rent' && item.rentalPeriod) {
      mappedItem.rentalPeriod = item.rentalPeriod;
    }
    
    return mappedItem;
  }).filter(item => item !== null); // Filter out any nulls if product was missing
};

export default enhancedCartApi;
